import React from 'react';
import { Alert, Box, Stack, Typography } from '@mui/material';
import PageSeo from '../seo/PageSeo';
import AdminLayout from '../components/AdminLayout';
import { PLANNED_LITTERS } from '../admin/plannedLitters';
import OwnerInviteCard from '../components/OwnerInviteCard';
import { OwnerInvite } from '../admin/inviteStore';
import { AdminInviteActions, useAdminInvites } from '../admin/useAdminInvites';
import {
    galiaAccentBarSx,
    galiaBodySx,
    galiaHeadlineSx,
    galiaSectionLabelSx,
} from '../theme/galiaUi';

const FamilyCards: React.FC<{
    owners: OwnerInvite[];
    emptyLabel: string;
    actions: AdminInviteActions;
    onMoveBack: (id: string) => void;
}> = ({ owners, emptyLabel, actions, onMoveBack }) => {
    if (owners.length === 0) {
        return (
            <Typography sx={{ color: 'rgba(26, 26, 26, 0.55)' }}>{emptyLabel}</Typography>
        );
    }

    return (
        <Stack spacing={1.5}>
            {owners.map((owner) => (
                <OwnerInviteCard
                    key={owner.id}
                    invite={owner}
                    actions={actions}
                    variant="litter"
                    onMoveBack={() => onMoveBack(owner.id)}
                />
            ))}
        </Stack>
    );
};

const AdminLitters: React.FC = () => {
    const {
        unplannedLitters,
        ownersForLitter,
        loading,
        error,
        setTag,
        clearTags,
        setDeposit,
        clearDeposit,
        uploadApplication,
        sendPortalInvite,
        remove,
        moveBack,
    } = useAdminInvites();
    const actions = {
        setTag,
        clearTags,
        setDeposit,
        clearDeposit,
        uploadApplication,
        sendPortalInvite,
        remove,
    };

    return (
        <>
            <PageSeo
                title="Planned litters | Admin | Galia Standard Poodles"
                description="Planned litters and potential puppy owners."
                path="/admin/litters"
                noIndex
            />
            <AdminLayout>
                <Box sx={{ maxWidth: 560 }}>
                    <Typography component="h1" sx={galiaHeadlineSx}>
                        Planned litters
                    </Typography>
                    <Box sx={galiaAccentBarSx} />
                    <Typography sx={{ ...galiaBodySx, mb: 5 }}>
                        Families you have moved onto a planned litter.
                    </Typography>
                    {error && (
                        <Alert severity="error" sx={{ mb: 2.5, borderRadius: 2 }}>
                            {error}
                        </Alert>
                    )}
                    {loading && (
                        <Typography sx={{ color: 'rgba(26, 26, 26, 0.55)', mb: 2 }}>
                            Loading invites…
                        </Typography>
                    )}

                    <Stack spacing={4}>
                        {PLANNED_LITTERS.map((litter) => {
                            const owners = ownersForLitter(litter.name);
                            return (
                                <Box key={litter.id}>
                                    <Typography component="h2" sx={galiaHeadlineSx}>
                                        {litter.name}
                                    </Typography>
                                    <Typography sx={{ ...galiaBodySx, mt: 1, mb: 0.5 }}>
                                        {litter.timing}
                                    </Typography>
                                    <Typography
                                        sx={{
                                            mb: 2,
                                            fontSize: '0.875rem',
                                            color: 'rgba(26, 26, 26, 0.5)',
                                        }}
                                    >
                                        {owners.length === 1
                                            ? '1 potential owner'
                                            : `${owners.length} potential owners`}
                                    </Typography>
                                    <FamilyCards
                                        owners={owners}
                                        emptyLabel="No families moved to this litter yet."
                                        actions={actions}
                                        onMoveBack={(id) => {
                                            void moveBack(id);
                                        }}
                                    />
                                </Box>
                            );
                        })}

                        {unplannedLitters.map((group) => (
                            <Box key={group.litter}>
                                <Typography component="h2" sx={galiaSectionLabelSx}>
                                    {group.litter}
                                </Typography>
                                <FamilyCards
                                    owners={group.owners}
                                    emptyLabel="No families invited for this litter yet."
                                    actions={actions}
                                    onMoveBack={(id) => {
                                        void moveBack(id);
                                    }}
                                />
                            </Box>
                        ))}
                    </Stack>
                </Box>
            </AdminLayout>
        </>
    );
};

export default AdminLitters;
